/**
 * Shell-wide state: the `?issuer=all|<issuerId>` filter (validated by the root route, retained
 * across links) and the shell preferences kept in localStorage (last issuer, sidebar collapsed).
 * Month screens keep their own month (`lib/months.ts`).
 */
import { useSearch } from '@tanstack/react-router';
import type { SearchSchemaInput } from '@tanstack/react-router';
import { readStored, writeStored } from '../../lib/storage';

export const ALL_ISSUERS = 'all';
const ISSUER_RE = /^[A-Za-z0-9_-]{1,64}$/;
const PREFS_KEY = 'outsource.shell';

export interface ShellSearch {
  readonly issuer: string;
}

export interface ShellPrefs {
  readonly issuer?: string;
  readonly sidebarCollapsed?: boolean;
}

function isPrefs(value: unknown): value is ShellPrefs {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function readPrefs(): ShellPrefs {
  return readStored(PREFS_KEY, isPrefs) ?? {};
}

export function writePrefs(patch: ShellPrefs): void {
  writeStored(PREFS_KEY, { ...readPrefs(), ...patch });
}

function isIssuerParam(value: unknown): value is string {
  return typeof value === 'string' && ISSUER_RE.test(value);
}

function defaultIssuer(): string {
  const stored = readPrefs().issuer;
  return isIssuerParam(stored) ? stored : ALL_ISSUERS;
}

/** Root route `validateSearch`: `issuer` is optional in links, always present when read. */
export function validateShellSearch(input: { issuer?: unknown } & SearchSchemaInput): ShellSearch {
  return { issuer: isIssuerParam(input.issuer) ? input.issuer : defaultIssuer() };
}

/** The issuer filter: `'all'` or an issuer id. */
export function useIssuer(): string {
  const search = useSearch({ strict: false }) as Partial<ShellSearch>;
  return isIssuerParam(search.issuer) ? search.issuer : defaultIssuer();
}

/** Issuer filter for API queries: `undefined` for all issuers, else the id (`?issuer_id=`). */
export function issuerIdParam(issuer: string): string | undefined {
  return issuer === ALL_ISSUERS ? undefined : issuer;
}
